// SMS is sent via a Cloudflare Worker proxy — API credentials stay server-side.
// Set REACT_APP_SMS_API_URL to your Worker URL in .env
import {
  auth,
  db,
  INCREMENT_TSAV_SMS_LIMIT_HTTP_URL,
} from "../config/firebase";
import { doc, getDoc } from "firebase/firestore";

async function getSmsRoleForWorker() {
  const email = auth.currentUser?.email;
  if (!email) return "childUser";
  try {
    const snap = await getDoc(doc(db, "users", email));
    if (snap.exists()) {
      return snap.data().permissions || "childUser";
    }
  } catch {
    /* ignore */
  }
  return "childUser";
}
const SMS_WORKER_URL = process.env.REACT_APP_SMS_API_URL || "";
const SMS_CHAR_LIMIT = parseInt(process.env.REACT_APP_SMS_CHAR_LIMIT || "268", 10);
const IS_TEST_MODE = (process.env.REACT_APP_SAP_ENVIRONMENT || "test").toLowerCase() === "test";
const PHONE_MAINTAIN = process.env.REACT_APP_PHONE_MAINTAIN || "";

function sanitizePhone(phone) {
  return phone.replace(/-/g, "").replace(/\s/g, "").trim();
}

async function sendSms(phone, employeeName, message) {
  if (!SMS_WORKER_URL) {
    console.warn("[SMS] REACT_APP_SMS_API_URL not configured");
    return { success: false, error: "SMS worker URL not configured" };
  }

  let destination = sanitizePhone(phone);
  if (IS_TEST_MODE && PHONE_MAINTAIN) {
    console.info(`[SMS] test mode — redirecting to: ${PHONE_MAINTAIN}`);
    destination = sanitizePhone(PHONE_MAINTAIN);
  }

  if (!destination || destination.length < 10) {
    return { success: false, error: "Invalid phone number" };
  }

  const trimmedMessage = message.length > SMS_CHAR_LIMIT
    ? message.slice(0, SMS_CHAR_LIMIT)
    : message;

  const userRole = await getSmsRoleForWorker();

  try {
    // Send to Cloudflare Worker — no credentials in the frontend
    const response = await fetch(SMS_WORKER_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        phone: destination,
        message: trimmedMessage,
        employeeName: employeeName || "",
        role: userRole,
      }),
    });

    if (response.ok) {
      console.info(`[SMS] sent successfully to ${destination}`);
      return { success: true };
    } else {
      const body = await response.text().catch(() => "");
      console.error(`[SMS] worker error ${response.status}:`, body);
      return { success: false, error: `SMS error ${response.status}` };
    }
  } catch (err) {
    console.error("[SMS] network error:", err);
    return { success: false, error: `SMS network error: ${err.message}` };
  }
}

/**
 * Send assignment/return confirmation SMS to an employee.
 * @param {string} phone - Employee phone (10 digits, e.g. 0541234567)
 * @param {string} employeeName - Full name in Hebrew
 * @param {string} url - Assignment page URL
 * @param {boolean} hasAssignments - True if items were assigned
 * @param {boolean} hasReturns - True if items were returned
 */
export async function sendAssignmentSms(phone, employeeName, url, hasAssignments, hasReturns) {
  let action;
  if (hasAssignments && hasReturns) {
    action = "חתמת על קבלה והחזרה של ציוד";
  } else if (hasReturns) {
    action = "חתמת על החזרת ציוד";
  } else {
    action = "חתמת על קבלת ציוד";
  }
  const message = `שלום ${employeeName}, ${action}. לצפייה בפרטים: ${url}`;
  return sendSms(phone, employeeName, message);
}

/**
 * Send a דוח צ' request SMS to an employee.
 * @param {string} phone - Employee phone
 * @param {string} employeeName - Full name in Hebrew
 * @param {string} url - Assignment page URL
 */
export async function sendTsavReportSms(phone, employeeName, url) {
  const message = `שלום ${employeeName}, נא לסמן דוח צ' בקישור המצורף: ${url}`;
  return sendSms(phone, employeeName, message);
}

/** Returns how many times the Tsav report SMS was sent today (from localStorage). */
export function getTsavDailyUsage() {
  const today = new Date().toISOString().slice(0, 10);
  try {
    const saved = JSON.parse(localStorage.getItem("kesher_tsav_sms_daily") || "{}");
    if (saved.date === today) return saved.count || 0;
    return 0;
  } catch {
    return 0;
  }
}

/** Increments the Tsav report SMS daily counter in localStorage. */
export function incrementTsavDailyUsage() {
  const today = new Date().toISOString().slice(0, 10);
  const count = getTsavDailyUsage() + 1;
  localStorage.setItem("kesher_tsav_sms_daily", JSON.stringify({ count, date: today }));
}

/**
 * Returns how many times the Tsav report SMS was sent today — shared across all users via Firestore.
 * Firestore doc: appConfig/tsavSmsLimit → { count: number, date: "YYYY-MM-DD" }
 */
export async function getTsavDailyUsageShared() {
  const today = new Date().toISOString().slice(0, 10);
  try {
    const snap = await getDoc(doc(db, "appConfig", "tsavSmsLimit"));
    if (!snap.exists()) return 0;
    const d = snap.data();
    return d.date === today ? (d.count || 0) : 0;
  } catch {
    return 0;
  }
}

/**
 * Increments the shared Tsav SMS daily counter via Cloud Function (Admin SDK).
 * Client cannot write appConfig/tsavSmsLimit — Firestore rules deny it.
 */
export async function incrementTsavDailyUsageShared() {
  const user = auth.currentUser;
  if (!user) {
    console.error("[SMS] increment tsav counter: not signed in");
    return;
  }
  try {
    const idToken = await user.getIdToken();
    const res = await fetch(INCREMENT_TSAV_SMS_LIMIT_HTTP_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({}),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      console.error(
        "[SMS] increment tsav counter failed",
        res.status,
        data.message || data.error,
      );
    }
  } catch (e) {
    console.error("[SMS] failed to increment tsav counter", e);
  }
}

const TSAV_SIGNATURE_SENT_KEY = "kesher_tsav_signature_sent_v1";

function tsavSignatureTodayRecord() {
  const today = new Date().toISOString().slice(0, 10);
  try {
    const raw = localStorage.getItem(TSAV_SIGNATURE_SENT_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    if (parsed.date === today && parsed.byTarget && typeof parsed.byTarget === "object") {
      return { date: today, byTarget: { ...parsed.byTarget } };
    }
  } catch {
    /* ignore */
  }
  return { date: today, byTarget: {} };
}

function normalizeTsavSignatureSenderKey(senderKey) {
  return String(senderKey || "unknown").trim().toLowerCase();
}

function normalizeTsavSignatureTargetPn(targetPersonalNumber) {
  return String(targetPersonalNumber ?? "").trim();
}

function tsavSignatureStorageCompositeKey(senderKey, targetPersonalNumber) {
  return `${normalizeTsavSignatureSenderKey(senderKey)}__${normalizeTsavSignatureTargetPn(targetPersonalNumber)}`;
}

/** Per signed-in user + target soldier: at most one Signature-page דוח צ SMS per calendar day (client-side). */
export function hasSentTsavFromSignatureToday(senderKey, targetPersonalNumber) {
  const target = normalizeTsavSignatureTargetPn(targetPersonalNumber);
  if (!target) return false;
  const { byTarget } = tsavSignatureTodayRecord();
  return !!byTarget[tsavSignatureStorageCompositeKey(senderKey, target)];
}

export function markTsavSentFromSignatureToday(senderKey, targetPersonalNumber) {
  const target = normalizeTsavSignatureTargetPn(targetPersonalNumber);
  if (!target) return;
  const { date, byTarget } = tsavSignatureTodayRecord();
  const key = tsavSignatureStorageCompositeKey(senderKey, target);
  localStorage.setItem(
    TSAV_SIGNATURE_SENT_KEY,
    JSON.stringify({
      date,
      byTarget: { ...byTarget, [key]: true },
    }),
  );
}
